import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { Svg, Path, G, Rect, Defs, ClipPath } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';
import Avatar from '@/components/Avatar';
import { trailRecentUsersService, TrailRecentUser } from '@/services/trailRecentUsersService';

export interface EloTrail {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  cover_image_url: string | null;
  step_count: number;
  order_index: number;
  clinic_id: number | null;
  categories?: string[];
  is_plus?: boolean;
}

export interface TrailProgress {
  completedCount: number;
  totalSteps: number;
  progressPercent: number;
  hasStarted?: boolean;
}

interface TrailCardProps {
  trail: EloTrail;
  progress?: TrailProgress;
  onPress: () => void;
}

const TRAIL_MARKER_ROWS = [
  [
    "M28.3509 54.9046L21.3594 20.6194L45.1606 16L42.1203 26.0105L48.6446 33.9597L25.1606 38.9259",
    "M67.6361 54.9046L60.6445 20.6194L84.4457 16L81.4054 26.0105L87.9297 33.9597L64.4458 38.9259",
    "M106.921 54.9046L99.9297 20.6194L123.731 16L120.691 26.0105L127.215 33.9597L103.731 38.9259",
    "M146.206 54.9046L139.215 20.6194L163.016 16L159.976 26.0105L166.5 33.9597L143.016 38.9259",
    "M185.492 54.9046L178.5 20.6194L202.301 16L199.261 26.0105L205.785 33.9597L182.301 38.9259",
    "M224.777 54.9046L217.785 20.6194L241.586 16L238.546 26.0105L245.07 33.9597L221.586 38.9259",
    "M264.062 54.9046L257.07 20.6194L280.872 16L277.831 26.0105L284.356 33.9597L260.872 38.9259",
    "M303.347 54.9046L296.355 20.6194L320.157 16L317.116 26.0105L323.641 33.9597L300.157 38.9259",
  ],
  [
    "M28.3509 105.809L21.3594 71.524L45.1606 66.9045L42.1203 76.9151L48.6446 84.8643L25.1606 89.8304",
    "M67.6361 105.809L60.6445 71.524L84.4457 66.9045L81.4054 76.9151L87.9297 84.8643L64.4458 89.8304",
    "M106.921 105.809L99.9297 71.524L123.731 66.9045L120.691 76.9151L127.215 84.8643L103.731 89.8304",
    "M146.206 105.809L139.215 71.524L163.016 66.9045L159.976 76.9151L166.5 84.8643L143.016 89.8304",
    "M185.492 105.809L178.5 71.524L202.301 66.9045L199.261 76.9151L205.785 84.8643L182.301 89.8304",
    "M224.777 105.809L217.785 71.524L241.586 66.9045L238.546 76.9151L245.07 84.8643L221.586 89.8304",
    "M264.062 105.809L257.07 71.524L280.872 66.9045L277.831 76.9151L284.356 84.8643L260.872 89.8304",
    "M303.347 105.809L296.355 71.524L320.157 66.9045L317.116 76.9151L323.641 84.8643L300.157 89.8304",
  ],
  [
    "M28.3509 156.714L21.3594 122.429L45.1606 117.809L42.1203 127.82L48.6446 135.769L25.1606 140.735",
    "M67.6361 156.714L60.6445 122.429L84.4457 117.809L81.4054 127.82L87.9297 135.769L64.4458 140.735",
    "M106.921 156.714L99.9297 122.429L123.731 117.809L120.691 127.82L127.215 135.769L103.731 140.735",
    "M146.206 156.714L139.215 122.429L163.016 117.809L159.976 127.82L166.5 135.769L143.016 140.735",
    "M185.492 156.714L178.5 122.429L202.301 117.809L199.261 127.82L205.785 135.769L182.301 140.735",
    "M224.777 156.714L217.785 122.429L241.586 117.809L238.546 127.82L245.07 135.769L221.586 140.735",
    "M264.062 156.714L257.07 122.429L280.872 117.809L277.831 127.82L284.356 135.769L260.872 140.735",
    "M303.347 156.714L296.355 122.429L320.157 117.809L317.116 127.82L323.641 135.769L300.157 140.735",
  ],
];

const TrailMarkerBackground = () => (
  <View style={styles.markerContainer} pointerEvents="none">
    <Svg width="345" height="150" viewBox="0 0 345 150" fill="none">
      <Defs>
        <ClipPath id="clip0_trail_bg">
          <Rect width="345" height="150" fill="white" />
        </ClipPath>
      </Defs>
      <G clipPath="url(#clip0_trail_bg)">
        <Rect width="345" height="150" fill="#E6EBF0" />
        {TRAIL_MARKER_ROWS.map((row, ri) =>
          row.map((d, ci) => (
            <G key={`${ri}-${ci}`} opacity={0.5}>
              <Path d={d} fill="#C0D1DB" />
              <Path d={d} stroke="#C0D1DB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </G>
          ))
        )}
      </G>
    </Svg>
  </View>
);

const GemIcon = ({ size = 16, color = '#669AB7' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <Path
      d="M1.75524 5.7637L5.40815 9.7771C5.72535 10.1259 6.2741 10.1259 6.59125 9.7771L10.2447 5.7632C10.5502 5.42805 10.5852 4.92698 10.3298 4.55191L8.8587 2.39099C8.6763 2.12343 8.37275 1.96289 8.04875 1.96289H3.95555C3.63156 1.96289 3.32849 2.12294 3.14606 2.3905L1.67059 4.55191C1.4147 4.92649 1.44973 5.42805 1.75524 5.7637Z"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M6.99847 1.96289L7.68197 4.55191C7.78117 4.92698 7.76757 5.42805 7.64887 5.7632L6.22887 9.7771C6.10577 10.1259 5.89222 10.1259 5.76912 9.7771L4.34912 5.7637C4.23042 5.42805 4.2168 4.92649 4.31653 4.55191L5.00197 1.96289"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path d="M1.50391 5.10156H10.4954" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export default function TrailCard({
  trail,
  progress,
  onPress,
}: TrailCardProps) {
  const { t } = useLanguage();
  const [recentUsers, setRecentUsers] = useState<TrailRecentUser[]>([]);

  useEffect(() => {
    let cancelled = false;
    trailRecentUsersService.getRecentUsers(trail.slug).then((users) => {
      if (!cancelled) setRecentUsers(users);
    });
    return () => { cancelled = true; };
  }, [trail.slug]);

  const pct = progress?.progressPercent ?? 0;
  const total = progress?.totalSteps ?? trail.step_count;
  const completed = progress?.completedCount ?? 0;
  const barWidth = `${pct}%`;
  const isPlus = !!trail.is_plus;

  return (
    <TouchableOpacity style={styles.card} onPress={onPress} activeOpacity={0.8}>
      {/* ── Header ── */}
      <View style={styles.header}>
        <View style={styles.headerBg}>
          {trail.cover_image_url ? (
            <Image
              source={{ uri: trail.cover_image_url }}
              style={StyleSheet.absoluteFill}
              contentFit="cover"
              transition={150}
              cachePolicy="memory-disk"
            />
          ) : (
            <TrailMarkerBackground />
          )}
        </View>
        <LinearGradient
          colors={['rgba(255,255,255,0)', '#FFFFFF']}
          locations={[0.25, 0.7]}
          style={styles.gradient}
        >
          <View style={styles.content}>
            <Text style={styles.title} numberOfLines={2}>{trail.name}</Text>
            <View style={styles.progressGroup}>
              <View style={styles.progressLabelRow}>
                <Text style={styles.progressLabel}>{t('trail.progressLabel')}</Text>
                <Text style={styles.progressPercent}>{pct}%</Text>
              </View>
              <View style={styles.progressBarTrack}>
                <View style={[styles.progressBarFill, { width: barWidth as any }]} />
              </View>
            </View>
          </View>
        </LinearGradient>
      </View>

      {/* ── Footer ── */}
      <View style={styles.footer}>
        <View style={styles.avatars}>
          {recentUsers.map((user, i) => (
            <View key={user.userId} style={{ marginLeft: i > 0 ? -8 : 0 }}>
              <Avatar size={32} outerBorderWidth={4} borderColor="#FFFFFF" uri={user.avatarUrl} useCache={false} />
            </View>
          ))}
        </View>

        <View style={styles.actions}>
          {isPlus && (
            <View style={styles.gemButton}>
              <GemIcon size={16} color="#669AB7" />
            </View>
          )}
          <TouchableOpacity
            style={styles.arrowButton}
            onPress={(e) => { e.stopPropagation(); onPress(); }}
            activeOpacity={0.7}
          >
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path d="M6 12L10 8L6 4" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
            </Svg>
          </TouchableOpacity>
        </View>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    overflow: 'hidden',
    width: 345,
  },
  header: {
    height: 150,
    position: 'relative',
    overflow: 'hidden',
  },
  headerBg: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#E6EBF0',
  },
  markerContainer: {
    ...StyleSheet.absoluteFillObject,
  },
  gradient: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
    padding: 24,
  },
  content: {
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28,
  },
  progressGroup: {
    gap: 8,
  },
  progressLabelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  progressLabel: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#669AB7',
    lineHeight: 19.6,
  },
  progressPercent: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    lineHeight: 19.6,
  },
  progressBarTrack: {
    width: '100%',
    height: 4,
    backgroundColor: '#F0EEE4',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: 4,
    backgroundColor: '#669AB7',
    borderRadius: 2,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 0,
  },
  avatars: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  gemButton: {
    backgroundColor: '#F6F7F9',
    borderRadius: 12,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  arrowButton: {
    backgroundColor: '#F9F8F3',
    borderRadius: 12,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
